import { isEmpty } from '@intelligence/v3/utils/common';
import { formatDateStr } from '@intelligence/v3/utils/date';
import { LANGUAGE_EN } from '@intelligence/_config/config';
import { buildDownloadDataNew, valueFormat } from '@intelligence/utils/commonUtils';
import { decimalFormat } from '@home/utils/common';

type TExportLanguage = 'cn' | 'en';

const getExportLanguage = (downloadLanguage?: string): TExportLanguage => {
  return downloadLanguage === LANGUAGE_EN || downloadLanguage === 'en'
    ? 'en'
    : 'cn';
};

const DIMENSION_CONFIG = [
  {
    key: 'wechat_popularity',
    titleCN: '微信小游戏人气榜',
    titleEN: 'WeChat Top Popular Rank',
  },
  {
    key: 'wechat_bestseller',
    titleCN: '微信小游戏畅销榜',
    titleEN: 'WeChat Best Selling Rank',
  },
  {
    key: 'wechat_most_played',
    titleCN: '微信小游戏畅玩榜',
    titleEN: 'WeChat Most Played Rank',
  },
  {
    key: 'douyin_popularity',
    titleCN: '抖音小游戏热门榜',
    titleEN: 'Douyin Top Popular Rank',
  },
  {
    key: 'douyin_bestseller',
    titleCN: '抖音小游戏畅销榜',
    titleEN: 'Douyin Best Selling Rank',
  },
  {
    key: 'douyin_fresh_game',
    titleCN: '抖音小游戏新游榜',
    titleEN: 'Douyin New Release Rank',
  },
];

interface IAggregatedDateRow {
  date: string;
  gameName: string;
  publisher?: string;
  minigameGenre?: string;
  minigameSubGenre?: string;
  ranks: Record<string, number | string>;
  rankChanges: Record<string, number | string>;
}

/**
 * 将接口返回的按 (date, minigame_type) 扁平列表聚合为按日期为维度的宽表结构
 */
const aggregateRowsByDate = (
  rawList: any[],
  language: TExportLanguage,
): IAggregatedDateRow[] => {
  if (isEmpty(rawList)) return [];

  const dateMap = new Map<string, IAggregatedDateRow>();

  rawList.forEach((item: any) => {
    const date = item?.date || item?.day || item?.time;
    if (!date) return;

    const gameName =
      (language === 'en' ? item?.app_name_en : item?.app_name) ||
      item?.app_name ||
      item?.app_name_en ||
      '-';

    const publisher =
      (language === 'en' ? item?.publisher_en : item?.publisher) ||
      item?.publisher ||
      item?.publisher_en ||
      item?.developer ||
      '-';

    const minigameGenre =
      (language === 'en' ? item?.minigame_genre_en : item?.minigame_genre) ||
      item?.minigame_genre ||
      item?.minigame_genre_en ||
      item?.genre ||
      '-';

    const minigameSubGenre =
      (language === 'en' ? item?.minigame_sub_genre_en : item?.minigame_sub_genre) ||
      item?.minigame_sub_genre ||
      item?.minigame_sub_genre_en ||
      item?.sub_genre ||
      '-';

    const dimension =
      item?.minigame_type || item?.data_source || item?.source || '';
    const rank = item?.game_rank ?? item?.rank ?? '-';
    const rankChange =
      item?.game_rank_dod ?? item?.rank_dod ?? item?.rank_change ?? '-';

    if (!dateMap.has(date)) {
      dateMap.set(date, {
        date,
        gameName,
        publisher,
        minigameGenre,
        minigameSubGenre,
        ranks: {},
        rankChanges: {},
      });
    }

    const row = dateMap.get(date)!;
    if (dimension) {
      row.ranks[dimension] = rank;
      row.rankChanges[dimension] = rankChange;
    }
    if (gameName && gameName !== '-' && row.gameName === '-') {
      row.gameName = gameName;
    }
    if (publisher && publisher !== '-' && row.publisher === '-') {
      row.publisher = publisher;
    }
    if (minigameGenre && minigameGenre !== '-' && row.minigameGenre === '-') {
      row.minigameGenre = minigameGenre;
    }
    if (minigameSubGenre && minigameSubGenre !== '-' && row.minigameSubGenre === '-') {
      row.minigameSubGenre = minigameSubGenre;
    }
  });

  return Array.from(dateMap.values());
};

export const getMinigameDetailsStoreRankConfig = (
  data: any,
  params: any,
  allParams: any,
) => {
  const rawList = Array.isArray(data?.list)
    ? data.list
    : Array.isArray(data?.data?.list)
      ? data.data.list
      : Array.isArray(data)
        ? data
        : [];

  const granularity = params?.granularity || 'daily';
  const language = getExportLanguage(
    allParams?.download_language || params?.download_language,
  );
  const selectedTypes: string[] =
    params?.minigame_types || allParams?.minigame_types || [];

  const isCompanyShow =
    allParams?.company_show === 1 ||
    allParams?.company_show === true ||
    allParams?.data_sources?.company_show;

  const isGenreShow =
    allParams?.genre_show === 1 ||
    allParams?.genre_show === true ||
    allParams?.data_sources?.genre_show;

  const aggregatedData = aggregateRowsByDate(rawList, language);

  // 默认最新日期置顶（按日期降序，对齐大宽表默认顺序）
  const sortedData = [...aggregatedData].sort((a, b) =>
    b.date.localeCompare(a.date),
  );

  const commonSheetConfig = [
    {
      title: language === 'en' ? 'Date' : '日期',
      key: (row: IAggregatedDateRow) => {
        const itemDate = row.date || '-';
        return granularity === 'monthly'
          ? formatDateStr(itemDate, 'YYYY-MM')
          : itemDate;
      },
    },
    {
      title: language === 'en' ? 'Game Name' : '游戏名称',
      key: (row: IAggregatedDateRow) => row.gameName || '-',
    },
    {
      title: language === 'en' ? 'Company Name' : '发行商',
      key: (row: IAggregatedDateRow) => row.publisher || '-',
      when: () => Boolean(isCompanyShow),
    },
    {
      title: language === 'en' ? 'Main Genre' : '主品类',
      key: (row: IAggregatedDateRow) => row.minigameGenre || '-',
      when: () => Boolean(isGenreShow),
    },
    {
      title: language === 'en' ? 'Sub Genre' : '子品类',
      key: (row: IAggregatedDateRow) => row.minigameSubGenre || '-',
      when: () => Boolean(isGenreShow),
    },
    ...DIMENSION_CONFIG.flatMap(dim => [
      {
        title: language === 'en' ? dim.titleEN : dim.titleCN,
        key: (row: IAggregatedDateRow) => valueFormat(row.ranks?.[dim.key]),
        when: () =>
          selectedTypes.length === 0 || selectedTypes.includes(dim.key),
      },
      {
        title: language === 'en' ? 'Rank Change' : '排名变化',
        key: (row: IAggregatedDateRow) => {
          const rawChange = row.rankChanges?.[dim.key];
          if (rawChange === '-' || rawChange === null || rawChange === undefined || rawChange === '') {
            return '-';
          }
          const num = Number(decimalFormat(rawChange, 1, false, ''));
          if (Number.isNaN(num)) {
            return rawChange;
          }
          if (num === 0) {
            return '=';
          }
          return num;
        },
        when: () =>
          selectedTypes.length === 0 || selectedTypes.includes(dim.key),
      },
    ]),
  ];

  return {
    stat: {
      sheetName: 'Store Rank',
      style: 'normal',
      data: buildDownloadDataNew(commonSheetConfig, sortedData),
    },
  };
};
